import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, FileText } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [modal, setModal] = useState<ModalType>(null);
  useEffect(() => {
    if (modal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modal]);
  return <>
      <footer className="bg-slate-950 py-12 border-t border-slate-800" data-id="element-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-21">
          <p className="text-slate-400 text-sm mb-4" data-id="element-22">
            © {new Date().getFullYear()} Adiós a la Fascia Plantar. Todos los
            derechos reservados.
          </p>

          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-23">
            <button onClick={() => setModal('privacy')} className="text-slate-400 hover:text-white text-sm transition-colors underline-offset-4 hover:underline" data-id="element-24">
              Política de Privacidad
            </button>
            <span className="text-slate-700" data-id="element-25">•</span>
            <button onClick={() => setModal('terms')} className="text-slate-400 hover:text-white text-sm transition-colors underline-offset-4 hover:underline" data-id="element-26">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-slate-500 text-xs max-w-3xl mx-auto leading-relaxed" data-id="element-27">
            Descargo de responsabilidad: La información contenida en este sitio
            web y en el manual es solo para fines educativos e informativos y no
            debe considerarse como consejo médico. Consulte siempre a un médico
            o profesional de la salud calificado antes de comenzar cualquier
            programa de ejercicios o tratamiento.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-28">
        {modal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setModal(null)} className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" data-id="element-29">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.2
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" data-id="element-30">
              <div className="flex items-center justify-between p-6 border-b border-slate-100 shrink-0" data-id="element-31">
                <div className="flex items-center gap-3" data-id="element-32">
                  <div className="bg-brand-50 text-brand-600 p-2 rounded-lg" data-id="element-33">
                    {modal === 'privacy' ? <ShieldCheck className="w-5 h-5" data-id="element-34" /> : <FileText className="w-5 h-5" data-id="element-35" />}
                  </div>
                  <h2 className="font-display font-bold text-xl text-slate-900" data-id="element-36">
                    {modal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                  </h2>
                </div>
                <button onClick={() => setModal(null)} aria-label="Cerrar" className="text-slate-400 hover:text-slate-900 hover:bg-slate-100 p-2 rounded-full transition-colors" data-id="element-37">
                  <X className="w-5 h-5" data-id="element-38" />
                </button>
              </div>

              <div className="p-6 md:p-8 overflow-y-auto text-slate-600 leading-relaxed text-sm space-y-4" data-id="element-39">
                {modal === 'privacy' ? <PrivacyContent data-id="element-40" /> : <TermsContent data-id="element-41" />}
              </div>

              <div className="p-4 border-t border-slate-100 shrink-0 bg-slate-50" data-id="element-42">
                <button onClick={() => setModal(null)} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-3 rounded-xl transition-colors" data-id="element-43">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-slate-500 text-xs" data-id="element-44">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
      </p>

      <h3 className="font-bold text-slate-900 text-base pt-2" data-id="element-45">
        1. Información que recopilamos
      </h3>
      <p data-id="element-46">
        Recopilamos la información que nos proporcionas directamente al realizar
        una compra, como tu nombre, correo electrónico y datos de pago. Los
        datos de pago son procesados por proveedores externos seguros y nunca
        son almacenados en nuestros servidores.
      </p>

      <h3 className="font-bold text-slate-900 text-base pt-2" data-id="element-47">
        2. Uso de la información
      </h3>
      <p data-id="element-48">
        Utilizamos tu información para procesar tu compra, enviarte el material
        digital adquirido, y ocasionalmente compartir contenido relacionado con
        salud y bienestar. Nunca vendemos ni compartimos tu información con
        terceros con fines comerciales.
      </p>

      <h3 className="font-bold text-slate-900 text-base pt-2" data-id="element-49">3. Cookies</h3>
      <p data-id="element-50">
        Nuestro sitio utiliza cookies para mejorar tu experiencia de navegación
        y analizar el tráfico. Puedes desactivar las cookies en la configuración
        de tu navegador en cualquier momento.
      </p>

      <h3 className="font-bold text-slate-900 text-base pt-2" data-id="element-51">4. Seguridad</h3>
      <p data-id="element-52">
        Implementamos medidas de seguridad técnicas y organizativas (cifrado SSL
        de 256 bits) para proteger tu información personal contra accesos no
        autorizados, alteración o divulgación.
      </p>

      <h3 className="font-bold text-slate-900 text-base pt-2" data-id="element-53">
        5. Tus derechos
      </h3>
      <p data-id="element-54">
        Tienes derecho a acceder, rectificar o eliminar tus datos personales en
        cualquier momento. Para ejercer estos derechos, puedes hacerlo a través
        de los canales habilitados en tu cuenta.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-slate-500 text-xs" data-id="element-55">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
      </p>

      <h3 className="font-bold text-slate-900 text-base pt-2" data-id="element-56">
        1. Aceptación de términos
      </h3>
      <p data-id="element-57">
        Al acceder y comprar productos en este sitio, aceptas estar sujeto a
        estos términos y condiciones. Si no estás de acuerdo, te pedimos no
        utilizar el sitio.
      </p>

      <h3 className="font-bold text-slate-900 text-base pt-2" data-id="element-58">
        2. Producto digital
      </h3>
      <p data-id="element-59">
        "Adiós a la Fascia Plantar" es un producto 100% digital (PDF). Tras la
        compra, recibirás acceso inmediato al material. No se enviarán productos
        físicos.
      </p>

      <h3 className="font-bold text-slate-900 text-base pt-2" data-id="element-60">
        3. Propiedad intelectual
      </h3>
      <p data-id="element-61">
        Todo el contenido del manual, incluyendo textos, gráficos, logos e
        imágenes, está protegido por derechos de autor. Queda prohibida la
        reproducción, distribución o reventa del material sin autorización
        expresa.
      </p>

      <h3 className="font-bold text-slate-900 text-base pt-2" data-id="element-62">
        4. Descargo médico
      </h3>
      <p data-id="element-63">
        El contenido tiene fines educativos e informativos. No sustituye el
        consejo, diagnóstico o tratamiento médico profesional. Consulta siempre
        a un profesional de la salud antes de iniciar cualquier programa de
        ejercicios.
      </p>

      <h3 className="font-bold text-slate-900 text-base pt-2" data-id="element-64">
        5. Garantía y reembolsos
      </h3>
      <p data-id="element-65">
        Ofrecemos una garantía de satisfacción. Si el producto no cumple tus
        expectativas, puedes solicitar un reembolso dentro del plazo establecido
        al momento de la compra, conforme a las políticas de la plataforma de
        pago.
      </p>

      <h3 className="font-bold text-slate-900 text-base pt-2" data-id="element-66">
        6. Limitación de responsabilidad
      </h3>
      <p data-id="element-67">
        No nos hacemos responsables por resultados específicos derivados de la
        aplicación del contenido, ya que cada caso es individual y depende del
        compromiso del usuario.
      </p>
    </>;
}